import React, { useState, useEffect, useCallback } from "react"
import { Modal, Button, Spinner, Badge } from "react-bootstrap"
import moment from "moment"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import MentionInput from "@/components/Common/MentionInput"
import MentionRenderer from "@/components/Common/MentionRenderer"
import { getOptimizedUrl } from "@/features/visits/ModernVisitGallery"

interface PhotoCommentModalProps {
  show: boolean
  onHide: () => void
  photo: any
  placeId?: string
}

export const PhotoCommentModal: React.FC<PhotoCommentModalProps> = ({
  show,
  onHide,
  photo,
  placeId,
}) => {
  const user = parsedUser()
  const [comments, setComments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [commentText, setCommentText] = useState("")
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])

  const visitId = photo?.placeVisitId || photo?.visitId || photo?.id || placeId

  const loadComments = useCallback(async () => {
    if (!visitId) return
    try {
      setLoading(true)
      const res = await API.getAction("PlaceVisitComment/List", `visitId=${visitId}`)
      if (res.data?.status === "ok") {
        setComments(res.data.response || [])
      }
    } catch (e) {
      console.warn("Error loading comments:", e)
    } finally {
      setLoading(false)
    }
  }, [visitId])

  useEffect(() => {
    if (show && visitId) {
      loadComments()
    }
  }, [show, visitId, loadComments])

  const handleSendComment = async () => {
    if (!commentText.trim() || !user?.ID || !visitId) return
    try {
      setSubmitting(true)
      const payload = {
        visitId: visitId,
        comment: commentText.trim(),
        accountId: String(user.ID),
        mentionedUserIds: mentionedUserIds,
      }
      const res = await API.postAction("PlaceVisitComment/AddEdit", payload)
      if (res.data?.status === "ok") {
        setCommentText("")
        setMentionedUserIds([])
        await loadComments()
      }
    } catch (e) {
      console.error("Error posting comment:", e)
    } finally {
      setSubmitting(false)
    }
  }

  const photoUrl = getOptimizedUrl(photo)
  const authorName = photo?.agentName || "Visita"
  const formattedDate = photo?.date ? moment(photo.date).format("DD/MM/YYYY h:mm A") : ""

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="photo-comment-modal">
      <Modal.Header closeButton className="border-bottom py-2.5 px-3 bg-light">
        <Modal.Title className="h6 mb-0 d-flex align-items-center gap-2">
          <i className="fas fa-comments text-primary" />
          <span>Comentarios y Menciones de la Foto</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="row g-0">
          {/* Left Column: Photo Preview */}
          <div className="col-12 col-md-5 bg-dark d-flex flex-column align-items-center justify-content-center p-3 text-center position-relative" style={{ minHeight: "260px" }}>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt="Foto"
                className="img-fluid rounded shadow-sm"
                style={{ maxHeight: "280px", objectFit: "contain" }}
              />
            ) : (
              <div className="text-white-50 small">Sin vista previa disponible</div>
            )}
            <div className="mt-2 text-white-50 small w-100 text-start px-2">
              <div className="fw-semibold text-white text-truncate">{authorName}</div>
              {formattedDate && <div style={{ fontSize: "0.72rem" }}>{formattedDate}</div>}
              {photo?.note && (
                <div className="mt-1 p-1.5 rounded bg-white bg-opacity-10 text-white small" style={{ fontSize: "0.75rem" }}>
                  <i className="fas fa-sticky-note me-1 text-warning" />
                  {photo.note}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Comments Feed */}
          <div className="col-12 col-md-7 d-flex flex-column" style={{ minHeight: "360px", maxHeight: "420px" }}>
            <div className="p-3 flex-grow-1 overflow-auto bg-white" style={{ minHeight: "260px" }}>
              {loading ? (
                <div className="text-center py-5 text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando comentarios...
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="far fa-comments d-block mb-2 text-secondary opacity-50" style={{ fontSize: "2rem" }} />
                  <p className="mb-1 fw-semibold small">Sin comentarios aún</p>
                  <small className="text-muted d-block">
                    Sé el primero en dejar un comentario o mencionar a un compañero usando <b>@</b>.
                  </small>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2.5">
                  {comments.map((c, i) => {
                    const commenterName = `${c.account?.firstName || "Usuario"} ${c.account?.lastName || ""}`.trim()
                    const timeAgo = c.created ? moment(c.created).fromNow() : ""
                    const isMe = String(c.accountId) === String(user?.ID)

                    return (
                      <div
                        key={c.id || i}
                        className={`p-2.5 rounded-3 border ${
                          isMe ? "bg-primary-subtle border-primary-subtle ms-4" : "bg-light me-4"
                        }`}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="fw-semibold small text-dark" style={{ fontSize: "0.82rem" }}>
                            {commenterName}
                          </span>
                          <span className="text-muted" style={{ fontSize: "0.68rem" }}>
                            {timeAgo}
                          </span>
                        </div>
                        <div className="text-break small" style={{ fontSize: "0.85rem", lineHeight: 1.4 }}>
                          <MentionRenderer text={c.comment} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Input Bar */}
            <div className="p-2.5 border-top bg-light">
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <MentionInput
                    value={commentText}
                    onChange={(val, ids) => {
                      setCommentText(val)
                      setMentionedUserIds(ids)
                    }}
                    placeholder="Escribe un comentario (@ para mencionar)..."
                    disabled={submitting}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault()
                        handleSendComment()
                      }
                    }}
                    className="form-control-sm border shadow-none bg-white rounded-pill px-3"
                    showQuickAtButton={true}
                  />
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  className="rounded-pill px-3 flex-shrink-0"
                  disabled={!commentText.trim() || submitting}
                  onClick={handleSendComment}
                >
                  {submitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-1" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default PhotoCommentModal
